// pages/retail-investor/Financial-highlight.tsx
import type { ReactElement } from 'react'
import styled from 'styled-components'

import { theme } from '@/styles/theme'

import { TitleLayout } from '@/components/layout'
import { CompanyLinks } from '@/components/layouts/Company/CompanyLinks'
import { Head } from '@/components/layouts/Head'

const FinancialHighlightPage = () => {
  return (
    <>
      <CompanyMain>
        <CompanyLinks padding={{ pc: '0 0', sp: '0 0' }} />
      </CompanyMain>
    </>
  )
}

export default FinancialHighlightPage

FinancialHighlightPage.getLayout = (page: ReactElement) => {
  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: '個人投資家の皆様へ', path: '/ir/investor/' },
    { name: '業績ハイライト', path: '' },
  ];

  return (
    <>
      <Head
        title="企業情報"
        description="トライアルホールディングスの企業情報を掲載しています。"
        ogp={{ url: 'company/' }}
      />
      <TitleLayout
        title="Investor Relations"
        subTitle="株主・投資家向け情報"
        breadCrumbs={breadCrumbs}
      >
        <FinancialSection>
          <FinancialTitle style={{ marginTop: '20px' }}>2026年６月期の業績</FinancialTitle>
          <FinancialScription>
            <span>●</span> 時代に合わせた戦略の推進を通じて26期連続増収を達成。<br />
            直近では西友M&Aにより成長ステージが一段拡大。
          </FinancialScription>
          <img style={{ marginTop: '30px', marginBottom: '20px' }} src="/images/financial_highlight_0.png" />
          <FinancialScription><span>●</span> 2026年６月期サマリー</FinancialScription>
          <FinancialScriptionRemark>
            西友の連結によりグループ規模が大きく拡大するとともに、トライアル・西友両社の既存店成長と、トライアルの収益性改善が進展。<br />
            連結売上高は初の１兆円超となり26期連続増収を達成。営業利益は前期比43.9％増の303億円と初の300億円を突破。西友連結による
            事業構成の変化に加え、トライアルのプライシング施策や惣菜・PB商品の強化等も寄与し、売上総利益率は23.8％(同3.3pt増)まで上昇。
          </FinancialScriptionRemark>

          <img
            style={{ marginTop: '30px', marginBottom: '30px' }}
            src="/images/financial_highlight_23.png"
            alt="2026年6月期 業績サマリー"
          />

          <FinancialScription><span>●</span>商品カテゴリ別の売上高構成比</FinancialScription>
          <img style={{ marginTop: '40px', marginBottom: '20px' }} src="/images/financial_highlight_15.png" />

          <FinancialScription><span>●</span>主要な指標の推移</FinancialScription>

          <FinancialChartGrid>
            <FinancialChartGridItem>
              <FinancialChartGridTit>売上高</FinancialChartGridTit>
              <FinancialChartGridImg src="/images/financial_highlight_18.png" alt="売上高" />
            </FinancialChartGridItem>
            <FinancialChartGridItem>
              <FinancialChartGridTit>営業利益</FinancialChartGridTit>
              <FinancialChartGridImg src="/images/financial_highlight_19.png" alt="営業利益" />
            </FinancialChartGridItem>
            <FinancialChartGridItem>
              <FinancialChartGridTit>
                EBITDA<sup>*1</sup><br />&nbsp;
              </FinancialChartGridTit>
              <FinancialChartGridImg src="/images/financial_highlight_20.png" alt="EBITDA" />
            </FinancialChartGridItem>
            <FinancialChartGridItem>
              <FinancialChartGridTit>
                親会社株主に帰属する<br />当期純利益
              </FinancialChartGridTit>
              <FinancialChartGridImg src="/images/financial_highlight_21.png" alt="親会社株主に帰属する当期純利益" />
            </FinancialChartGridItem>
          </FinancialChartGrid>

          <FinancialChartRemark>
            <p>*1 EBITDA＝「営業利益」＋「減価償却費」＋「のれん償却費」</p>
            <p>*2 2026年６月期は、西友の完全子会社化に伴うのれん償却や支払利息に加え、M&A・資金調達に係る一過性費用等の影響により、当期純利益は35億円となりました</p>
          </FinancialChartRemark>

          <FinancialTitle>株主還元</FinancialTitle>
          <FinancialScription style={{ marginTop: '20px', fontSize: '22px' }}>
            <span>●</span> 成長投資を優先しつつ、安定的かつ継続的な配当を実施(<HighlightBlue>累進配当</HighlightBlue>)
          </FinancialScription>

          <DividendImage
            src="/images/financial_highlight_22.png"
            alt="1株当たり配当と株主還元方針"
          />
        </FinancialSection>
      </TitleLayout>
    </>
  )
}

const CompanyMain = styled.div`
  width: 1040px;
  margin: 0 auto;
  padding: 98px 0 130px;

  @media screen and (max-width: 1047px) {
    width: 100%;
    padding: 40px 20px 70px;
  }
`

const FinancialSection = styled.div`
  width: 1040px;
  margin: 0 auto;
  padding: 30px 0 130px;
  img {
    width: 100%;
  }
  @media screen and (max-width: 1047px) {
    width: 100%;
    padding: 40px 20px 70px;
  }
`

const FinancialTitle = styled.div`
  width: 100%;
  height: 60px;
  line-height: 58px;    
  font-size: 32px;
  font-weight: bold;
  text-indent: 10px;
  color: #FFF;
  margin-top: 70px;
  background: ${theme.colors.trialBlue};
  @media screen and (max-width: 1047px) {
    font-size: 17px;
    height: 40px;
    line-height: 38px;
    margin-top: 100px;
  }
`

const FinancialScription = styled.div`
  margin-top: 30px;
  font-size: 26px;
  line-height: 34px;
  font-weight: bold;
  overflow: hidden;
  span {
    margin-right: 8px;
  }
  p {
    margin-top: 10px;
    font-size: 20px;
    letter-spacing: 1px;
  }
  @media screen and (max-width: 1047px) {
    font-size: 16px !important;
    line-height: 23px;
    margin-top: 12px;
    span {
      margin-right: 5px;
    }
    p {
      font-size: 14px !important;
      line-height: 28px;
    }
  }
`

const HighlightBlue = styled.span`
  color: ${theme.colors.trialBlue};
  font-weight: bold;
  margin-right: 0 !important;
`

const FinancialScriptionRemark = styled.p`
  margin-top: 20px;
  font-size: 17px;
  line-height: 40px;
  @media screen and (max-width: 1047px) {
    font-size: 14px;
    line-height: 26px;
    margin-top: 12px;
  }
`

const FinancialChartGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 80px;
  margin-top: 30px;

  @media screen and (max-width: 767px) {
    gap: 20px;
    margin-top: 20px;
  }
`

const FinancialChartGridItem = styled.div`
  display: flex;
  flex-direction: column;
`

const FinancialChartGridImg = styled.img`
  width: 380px !important;
  height: 420px !important;
  object-fit: contain;
  display: block;
  margin-left: 68px;

  @media screen and (max-width: 1047px) {
    width: 100% !important;
    height: auto !important;
    max-width: 380px;
    margin-left: 0;
  }
`

const FinancialChartGridTit = styled.div`
  font-size: 26px;
  font-weight: bold;
  line-height: 28px;
  margin-bottom: 10px;
  sup {
    font-size: 12px;
    vertical-align: super;
  }
  @media screen and (max-width: 767px) {
    font-size: 16px;
    line-height: 24px;
  }
`

const DividendImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  margin-top: 30px;
`

const FinancialChartRemark = styled.div`
  margin-top: 20px;
  font-size: 14px;
  line-height: 18px;
  color: #333;
  p {
    margin: 0;
    padding-left: 16px;
    text-indent: -16px;
  }
  @media screen and (max-width: 1047px) {
    font-size: 10px;
    line-height: 14px;
  }
`